/**
 * API client.
 *
 * Every call carries a fresh Firebase ID token. On a 401 the token is refreshed
 * once and the call retried, which covers the ordinary case of a token expiring
 * mid-session — without that, a user who left a tab open would see spurious
 * failures.
 *
 * Share counts and money arrive as *strings* and stay strings all the way to the
 * formatter. Never JSON.parse them into Numbers and never do arithmetic on them
 * here: a cap table holds values outside the exact range of a double, and the
 * server is deliberately exact. Derived figures come from the API, not from
 * client-side maths.
 */

import { getIdToken } from './firebase';

export class ApiError extends Error {
  constructor(message, { status, detail, issues, requestId } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.issues = issues;
    this.requestId = requestId;
  }

  /** Field-keyed validation messages, for rendering inline on a form. */
  get fieldErrors() {
    if (!Array.isArray(this.issues)) return {};
    return this.issues.reduce((acc, issue) => {
      acc[issue.path] = issue.message;
      return acc;
    }, {});
  }
}

async function request(method, path, body, { retryOn401 = true } = {}) {
  const token = await getIdToken();

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });

  if (response.status === 401 && retryOn401) {
    // Force a refresh and try once more before surfacing an auth failure.
    await getIdToken(true);
    return request(method, path, body, { retryOn401: false });
  }

  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: text.slice(0, 400) };
    }
  }

  if (!response.ok) {
    throw new ApiError(payload?.error ?? `Request failed (${response.status})`, {
      status: response.status,
      detail: payload?.detail,
      issues: payload?.issues,
      requestId: payload?.requestId,
    });
  }

  return payload;
}

const get = (path) => request('GET', path);
const post = (path, body) => request('POST', path, body);
const patch = (path, body) => request('PATCH', path, body);
const put = (path, body) => request('PUT', path, body);
const del = (path) => request('DELETE', path);

function query(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  // ---- identity ----
  me: () => get('/me'),
  refreshClaims: () => post('/me/refresh-claims'),
  signOutServer: () => post('/me/sign-out'),

  // ---- companies ----
  createCompany: (body) => post('/companies', body),
  company: (id) => get(`/companies/${id}`),
  updateCompany: (id, body) => patch(`/companies/${id}`, body),

  members: (id) => get(`/companies/${id}/members`),
  // 200 when the address already has an account, 202 when an invitation was
  // recorded and emailed instead. The response carries `pending` either way.
  upsertMember: (id, body) => put(`/companies/${id}/members`, body),
  removeMember: (id, userId) => del(`/companies/${id}/members/${userId}`),
  invitations: (id) => get(`/companies/${id}/invitations`),
  revokeInvitation: (id, invitationId) => del(`/companies/${id}/invitations/${invitationId}`),
  resendInvitation: (id, invitationId) =>
    post(`/companies/${id}/invitations/${invitationId}/resend`, {}),

  // ---- stakeholders ----
  stakeholders: (id, params) => get(`/companies/${id}/stakeholders${query(params)}`),
  stakeholder: (id, sid) => get(`/companies/${id}/stakeholders/${sid}`),
  createStakeholder: (id, body) => post(`/companies/${id}/stakeholders`, body),
  updateStakeholder: (id, sid, body) => patch(`/companies/${id}/stakeholders/${sid}`, body),

  // ---- equity structure ----
  shareClasses: (id) => get(`/companies/${id}/share-classes`),
  createShareClass: (id, body) => post(`/companies/${id}/share-classes`, body),
  updateShareClass: (id, cid, body) => patch(`/companies/${id}/share-classes/${cid}`, body),

  equityPlans: (id) => get(`/companies/${id}/equity-plans`),
  createEquityPlan: (id, body) => post(`/companies/${id}/equity-plans`, body),
  updateEquityPlan: (id, planId, body) => patch(`/companies/${id}/equity-plans/${planId}`, body),
  adjustPool: (id, planId, body) =>
    post(`/companies/${id}/equity-plans/${planId}/pool-adjustments`, body),

  vestingSchedules: (id) => get(`/companies/${id}/vesting-schedules`),
  createVestingSchedule: (id, body) => post(`/companies/${id}/vesting-schedules`, body),
  updateVestingSchedule: (id, scheduleId, body) => patch(`/companies/${id}/vesting-schedules/${scheduleId}`, body),

  // ---- securities ----
  securities: (id, params) => get(`/companies/${id}/securities${query(params)}`),
  security: (id, sid) => get(`/companies/${id}/securities/${sid}`),
  issueSecurity: (id, body) => post(`/companies/${id}/securities`, body),
  updateSecurity: (id, sid, body) => patch(`/companies/${id}/securities/${sid}`, body),
  transferSecurity: (id, sid, body) => post(`/companies/${id}/securities/${sid}/transfer`, body),
  cancelSecurity: (id, sid, body) => post(`/companies/${id}/securities/${sid}/cancel`, body),
  exerciseSecurity: (id, sid, body) => post(`/companies/${id}/securities/${sid}/exercise`, body),

  // ---- rounds ----
  rounds: (id) => get(`/companies/${id}/rounds`),
  round: (id, rid) => get(`/companies/${id}/rounds/${rid}`),
  createRound: (id, body) => post(`/companies/${id}/rounds`, body),
  updateRound: (id, rid, body) => patch(`/companies/${id}/rounds/${rid}`, body),
  closeRound: (id, rid, body) => post(`/companies/${id}/rounds/${rid}/close`, body),
  cancelRound: (id, rid) => post(`/companies/${id}/rounds/${rid}/cancel`, {}),

  // ---- derived views ----
  capTable: (id, params) => get(`/companies/${id}/cap-table${query(params)}`),
  myPosition: (id) => get(`/companies/${id}/cap-table/my-position`),
  waterfall: (id, params) => get(`/companies/${id}/cap-table/waterfall${query(params)}`),
  exportWaterfall: (id, params) =>
    downloadFile(`/companies/${id}/cap-table/waterfall/export${query(params)}`, 'exit-waterfall.xlsx'),
  waterfallCurve: (id, body) => post(`/companies/${id}/cap-table/waterfall-curve`, body),
  modelRound: (id, body) => post(`/companies/${id}/cap-table/model-round`, body),
  exportModelRound: (id, params) =>
    downloadFile(`/companies/${id}/cap-table/model-round/export${query(params)}`, 'modeled-round.xlsx'),
  reconcile: (id) => get(`/companies/${id}/cap-table/reconcile`),
  exportCapTable: (id, format = 'xlsx') =>
    downloadFile(`/companies/${id}/cap-table/export${query({ format })}`, `cap-table-export.${format}`),

  // ---- valuations, documents, audit ----
  valuations: (id) => get(`/companies/${id}/valuations`),
  createValuation: (id, body) => post(`/companies/${id}/valuations`, body),
  updateValuation: (id, vid, body) => patch(`/companies/${id}/valuations/${vid}`, body),

  documents: (id, params) => get(`/companies/${id}/documents${query(params)}`),
  reserveDocument: (id, body) => post(`/companies/${id}/documents`, body),
  updateDocument: (id, did, body) => patch(`/companies/${id}/documents/${did}`, body),
  confirmDocument: (id, did) => post(`/companies/${id}/documents/${did}/confirm`),
  documentDownload: (id, did) => get(`/companies/${id}/documents/${did}/download`),
  deleteDocument: (id, did) => del(`/companies/${id}/documents/${did}`),

  auditLog: (id, params) => get(`/companies/${id}/audit-log${query(params)}`),
  exportAuditLog: (id, params) =>
    downloadFile(`/companies/${id}/audit-log/export${query(params)}`, 'audit-log-export.xlsx'),

  // ---- compliance ----
  compliance83bWarnings: (id) => get(`/companies/${id}/compliance/83b-warnings`),
  markExercise83bFiled: (id, exerciseId) =>
    post(`/companies/${id}/exercises/${exerciseId}/mark-83b-filed`, {}),

  // ---- review queue ----
  // Proposals are what an agent produces; nothing here changes the cap table until
  // a human approves, and approving runs the same service a manual edit would.
  proposals: (id, params) => get(`/companies/${id}/proposals${query(params)}`),
  approveProposal: (id, proposalId) => post(`/companies/${id}/proposals/${proposalId}/approve`, {}),
  rejectProposal: (id, proposalId, note) =>
    post(`/companies/${id}/proposals/${proposalId}/reject`, { note }),
  proposalBatch: (id, batchId) => get(`/companies/${id}/proposals/batches/${batchId}`),
  // 200 when the whole plan applied, 409 when it stopped partway. The body
  // distinguishes what landed from what did not either way.
  approveBatch: (id, batchId) => post(`/companies/${id}/proposals/batches/${batchId}/approve`, {}),

  // ---- document ingestion ----
  extractDocument: (id, documentId) => post(`/companies/${id}/documents/${documentId}/extract`, {}),
  planFromDocuments: (id, documentIds) =>
    post(`/companies/${id}/cap-table/plan-from-documents`, { documentIds }),

  // ---- admin agent ----
  agentChat: (id, message, messages) => post(`/companies/${id}/agent/chat`, { message, messages }),
};

/**
 * Download an authenticated binary response (a generated file, not JSON) and
 * save it client-side. Bypasses `request()` entirely — the body is a file,
 * and there is nothing to JSON.parse.
 */
export async function downloadFile(path, filename) {
  const token = await getIdToken();
  const response = await fetch(`/api/v1${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'same-origin',
  });

  if (!response.ok) {
    const text = await response.text();
    let payload = null;
    try {
      payload = JSON.parse(text);
    } catch {
      // Not JSON — fall through with no parsed detail.
    }
    throw new ApiError(payload?.error ?? `Download failed (HTTP ${response.status})`, {
      status: response.status,
      detail: payload?.detail,
    });
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Upload straight to GCS with the signed URL the API minted. The bytes never
 * pass through the API pod — that is the whole point of the two-phase flow.
 */
export async function uploadToSignedUrl(uploadUrl, file) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) {
    throw new ApiError(`Upload failed (HTTP ${response.status})`, { status: response.status });
  }
}
